import { useEffect, useState } from 'react'
import { useParams, useNavigate, Navigate } from 'react-router-dom'
import { useSurvey } from '../store/surveyStore.js'
import { PRORES, SCALES, GROUPS, LIKERT } from '../surveys/prores-2-5.js'
import SurveyShell from '../components/survey/SurveyShell.jsx'
import LikertBatteryStep from '../components/survey/LikertBatteryStep.jsx'
import DemographicsStep from '../components/survey/Demographics.jsx'

export default function ProResFlow() {
  const { accessId } = useParams()
  const nav = useNavigate()
  const survey = useSurvey()
  const [validation, setValidation] = useState(null)

  useEffect(() => {
    survey.load(accessId)
    return () => survey.reset()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessId])

  if (survey.status === 'idle') return <div className="text-center py-12 text-ink-soft">Lade…</div>
  if (survey.status === 'completed') return <Navigate to={`/survey/${accessId}/result`} replace />

  const TOTAL_STEPS = PRORES.totalSteps
  const goNext = async () => {
    setValidation(null)
    const next = survey.step + 1
    if (next > TOTAL_STEPS) return
    survey.setStep(next)
    await survey.save()
  }
  const goBack = async () => {
    setValidation(null)
    if (survey.step <= 0) return
    survey.setStep(survey.step - 1)
    await survey.save()
  }
  const onFinish = async () => {
    setValidation(null)
    await survey.finish()
    nav(`/survey/${accessId}/result`)
  }

  const step = survey.step
  const data = survey.data
  const answers = data.answers || {}
  const shellProps = {
    logoNode: (
      <span className="font-serif text-[20px] sm:text-[24px] font-medium tracking-[-0.015em] text-ink leading-none">
        ProRes
      </span>
    ),
  }

  // 0: Welcome
  if (step === 0) {
    return (
      <SurveyShell
        step={0} total={TOTAL_STEPS} hideBack
        title={PRORES.welcome.headline}
        intro={null}
        onNext={goNext}
        nextLabel="Start"
        {...shellProps}
      >
        <h2 className="text-xl text-ink-soft -mt-2 mb-4">{PRORES.welcome.subheadline}</h2>
        {PRORES.welcome.body.map((p, i) => (
          <p key={i} className="text-ink leading-relaxed mb-3">{p}</p>
        ))}
      </SurveyShell>
    )
  }

  // 1: Demographics
  if (step === 1) {
    return (
      <DemographicsStep
        step={1} total={TOTAL_STEPS}
        data={data.demographics}
        onChange={(patch) => survey.setDemographics(patch)}
        onBack={goBack} onNext={goNext}
        validation={validation} setValidation={setValidation}
        {...shellProps}
      />
    )
  }

  const stepDef = PRORES.steps[step]
  if (!stepDef) {
    return <SurveyShell step={step} total={TOTAL_STEPS} {...shellProps}>
      <div className="text-rose-700">Unbekannter Schritt: {step}</div>
    </SurveyShell>
  }

  const isLast = step === TOTAL_STEPS

  if (stepDef.kind === 'intro') {
    const group = GROUPS.find(g => g.id === stepDef.groupId)
    return (
      <SurveyShell
        step={step} total={TOTAL_STEPS}
        title={group.title}
        intro={group.hint}
        onBack={goBack} onNext={goNext}
        {...shellProps}
      >
        <p className="text-ink-soft leading-relaxed">
          Es folgen {group.scaleIds.length} Skalen mit jeweils 2–7 Aussagen. Bitte bewerten Sie jede Aussage
          auf der 6-stufigen Skala.
        </p>
      </SurveyShell>
    )
  }

  if (stepDef.kind === 'scale') {
    const scale = SCALES[stepDef.scaleId]
    const allAnswered = scale.items.every(it => answers[it.key])
    const requireAndAdvance = () => {
      if (!allAnswered) {
        setValidation('Bitte bewerten Sie alle Aussagen auf dieser Seite, bevor Sie fortfahren.')
        return
      }
      setValidation(null)
      if (isLast) onFinish()
      else goNext()
    }
    return (
      <SurveyShell
        step={step} total={TOTAL_STEPS}
        onBack={goBack}
        onNext={requireAndAdvance}
        nextLabel={isLast ? 'Abschließen' : 'Weiter'}
        nextVariant={isLast ? 'primary' : 'secondary'}
        validation={validation}
        {...shellProps}
      >
        <LikertBatteryStep
          scale={scale}
          values={answers}
          likert={LIKERT}
          hint="1 = Trifft überhaupt nicht zu  ·  6 = Trifft völlig zu"
          onChange={(key, n) => survey.setAnswer(key, n)}
        />
      </SurveyShell>
    )
  }

  return <div>Unsupported step kind: {stepDef.kind}</div>
}
